/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { type CSSProperties } from "react";
import { Link } from "../components/ui/Link";
import { Building2, ChevronRight, Users } from "lucide-react";
import { listOrganizations, type OrganizationSummary } from "../api/registry";
import { useChrome } from "../contexts/ChromeContext";
import { Badge } from "../components/ui/Badge";
import { RegistryDirectory } from "../components/RegistryDirectory";
import { partnerTypeLabel } from "../lib/registry";

export function ReferralOrganizations() {
  useChrome({}, []);
  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Referring organizations
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          Every practice, school, and health plan that refers families to Manatee. Open one to see
          its providers and correct its details.
        </p>
      </header>
      <RegistryDirectory
        countNoun="organization"
        placeholder="Search organizations by name"
        load={listOrganizations}
        itemKey={(organization) => organization.id}
        renderCard={(organization, index) => (
          <OrganizationCard key={organization.id} organization={organization} index={index} />
        )}
        empty={{
          icon: <Building2 size={20} />,
          title: "No organizations match",
          description: "Try another name or state.",
        }}
      />
    </div>
  );
}

function OrganizationCard({
  organization,
  index,
}: {
  organization: OrganizationSummary;
  index: number;
}) {
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
    >
      <div className="min-w-0">
        <h2 className="truncate font-display text-xl leading-tight text-text-primary">
          <Link
            to={`/referrals/organizations/${organization.id}`}
            className="after:absolute after:inset-0 after:rounded-3xl"
          >
            {organization.name}
          </Link>
        </h2>
        <p className="mt-0.5 truncate text-xs text-text-tertiary">
          {partnerTypeLabel(organization.partnerType)}
          {organization.stateCode !== null && ` · ${organization.stateCode}`}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {organization.canReceiveUpdates && <Badge tone="success">Receives updates</Badge>}
      </div>
      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
        <span className="inline-flex items-center gap-1.5 text-text-tertiary">
          <Users size={12} />
          {organization.providerCount === 1
            ? "1 provider"
            : `${organization.providerCount} providers`}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
          Open
          <ChevronRight
            size={14}
            className="transition-transform duration-200 group-hover:translate-x-1"
          />
        </span>
      </div>
      <Building2 size={16} className="absolute right-5 top-5 text-text-tertiary" aria-hidden />
    </li>
  );
}
